import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatPaise, timeAgo } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { apiErrorMessage } from '@shared/api/client';
import { getInvoiceDownloadUrl } from '@shared/api/invoices';
import type { InvoicePaymentMode, InvoiceStatus, InvoiceSummary, Uuid } from '@shared/types/api';
import {
  useAdminInvoice,
  useAdminInvoices,
  useCancelInvoice,
  useMarkInvoicePaidOffline,
  useMarkInvoiceSent,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

const TABS: { value: InvoiceStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Sent' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
];

const TONE: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-amber-50 text-amber-800',
  PAID: 'bg-brand-50 text-brand-800',
  CANCELLED: 'bg-red-50 text-red-700',
  REFUNDED: 'bg-purple-50 text-purple-800',
};

const MODE_TONE: Record<InvoicePaymentMode, string> = {
  ONLINE: 'bg-blue-50 text-blue-800',
  OFFLINE: 'bg-gray-100 text-gray-700',
};

// Invoice creation is NOT initiated from this page — it lives on the
// transaction detail because a DRAFT invoice is really a state transition
// on the tx (INITIATED → INVOICE_CREATED). Creating from here would let an
// admin bypass that gate.
type OpenPanel =
  | { kind: 'view'; invoice: InvoiceSummary }
  | { kind: 'cancel'; id: Uuid }
  | { kind: 'payOffline'; id: Uuid; invoiceNumber: string }
  | null;

export function AdminInvoicesPage() {
  const [tab, setTab] = useState<InvoiceStatus | 'ALL'>('ALL');
  const [panel, setPanel] = useState<OpenPanel>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { canEdit, canDelete, isSuperAdmin } = useSectionPermission('INVOICES');

  const { data, isLoading } = useAdminInvoices({
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  });
  const markSent = useMarkInvoiceSent();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Invoices</h1>
        <p className="text-sm text-gray-600">
          Invoices are generated from a transaction in the{' '}
          <Link to="/transactions" className="text-brand-700 underline">
            Transactions
          </Link>{' '}
          tab when its status is <strong>INITIATED</strong>. This page lists every invoice
          and lets you view details, download PDFs, or manage status.
        </p>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setTab(t.value);
                setPage(1);
              }}
              className={cn(
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
                tab === t.value ? 'bg-brand-700 text-white shadow-xs' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60 sm:border-0',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="hidden sm:block shrink-0">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isLoading={isLoading}
            showBorder={false}
            showRange={false}
            showPageSize={false}
            compact
          />
        </div>
      </div>

      <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-sm text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Invoice #</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Tx</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Mode</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Subtotal</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">GST</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Total</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Created</th>
                <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={9} className="p-6 text-center text-gray-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-gray-500">No invoices.</td></tr>
              ) : items.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap">
                    <button
                      type="button"
                      className="text-gray-900 hover:text-brand-700 hover:underline text-left font-mono"
                      onClick={() => setPanel({ kind: 'view', invoice: inv })}
                      title="Click to view invoice details"
                    >
                      {inv.invoiceNumber}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                    <Link
                      to={`/transactions/${inv.transactionId}`}
                      className="text-brand-700 hover:underline"
                      title={`View transaction ${inv.transactionId}`}
                    >
                      {inv.transactionId.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', TONE[inv.status])}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', MODE_TONE[inv.paymentMode])}>
                      {inv.paymentMode}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatPaise(inv.subtotalPaise)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatPaise(inv.gstPaise)}</td>
                  <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatPaise(inv.totalPaise)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{timeAgo(inv.createdAt)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                    <div className="flex flex-wrap justify-end gap-2 text-xs">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:text-brand-800 hover:underline"
                        onClick={() => setPanel({ kind: 'view', invoice: inv })}
                      >
                        View
                      </button>
                      {inv.status === 'DRAFT' && (canEdit || isSuperAdmin) ? (
                        <button
                          className="text-brand-700 hover:underline"
                          onClick={() => markSent.mutate(inv.id)}
                          disabled={markSent.isPending}
                        >
                          Mark sent
                        </button>
                      ) : null}
                      {inv.paymentMode === 'OFFLINE' && (inv.status === 'DRAFT' || inv.status === 'SENT') && (canEdit || isSuperAdmin) ? (
                        <button
                          className="font-medium text-brand-700 hover:underline"
                          onClick={() =>
                            setPanel({ kind: 'payOffline', id: inv.id, invoiceNumber: inv.invoiceNumber })
                          }
                        >
                          Mark paid offline
                        </button>
                      ) : null}
                      {(inv.status === 'DRAFT' || inv.status === 'SENT') && (canDelete || isSuperAdmin) ? (
                        <button
                          className="text-red-600 hover:underline"
                          onClick={() => setPanel({ kind: 'cancel', id: inv.id })}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          isLoading={isLoading}
        />
      </section>

      {panel?.kind === 'view' ? (
        <ViewInvoiceDialog invoice={panel.invoice} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'cancel' ? (
        <CancelInvoiceDialog id={panel.id} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'payOffline' ? (
        <MarkPaidOfflineDialog
          id={panel.id}
          invoiceNumber={panel.invoiceNumber}
          onClose={() => setPanel(null)}
        />
      ) : null}
    </div>
  );
}

// Invoice creation moved to admin/features/admin/create-invoice-dialog.tsx
// and is triggered from the transaction detail page. See that file.

function CancelInvoiceDialog({ id, onClose }: { id: Uuid; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const cancelMut = useCancelInvoice();
  return (
    <Dialog
      open
      onClose={onClose}
      title="Cancel invoice"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Back</Button>
          <Button
            onClick={() =>
              cancelMut.mutate({ id, reason: reason.trim() }, { onSuccess: onClose })
            }
            disabled={reason.trim().length < 1 || cancelMut.isPending}
          >
            {cancelMut.isPending ? 'Cancelling…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        Paid invoices can&rsquo;t be cancelled — issue a refund instead.
      </p>
      <Input className="mt-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" />
      {cancelMut.isError ? (
        <p className="mt-2 text-sm text-red-600">{apiErrorMessage(cancelMut.error)}</p>
      ) : null}
    </Dialog>
  );
}

function MarkPaidOfflineDialog({
  id,
  invoiceNumber,
  onClose,
}: {
  id: Uuid;
  invoiceNumber: string;
  onClose: () => void;
}) {
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const mut = useMarkInvoicePaidOffline();

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Mark ${invoiceNumber} paid (offline)`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              mut.mutate(
                { id, reference: reference.trim(), notes: notes.trim() || undefined },
                { onSuccess: onClose },
              )
            }
            disabled={reference.trim().length < 3 || mut.isPending}
          >
            {mut.isPending ? 'Saving…' : 'Confirm paid'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        Record that you&rsquo;ve received payment out-of-band. This flips the invoice to
        <strong> PAID</strong> and moves the transaction forward — same side-effects as the
        Razorpay webhook.
      </p>
      <div className="mt-3 space-y-3 text-sm">
        <L label="Bank / UPI reference *">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR1234567890" />
        </L>
        <L label="Notes (optional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payer name, channel, etc." />
        </L>
        {mut.isError ? <p className="text-sm text-red-600">{apiErrorMessage(mut.error)}</p> : null}
      </div>
    </Dialog>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
    </label>
  );
}

function ViewInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: InvoiceSummary;
  onClose: () => void;
}) {
  const { data: detail, isLoading } = useAdminInvoice(invoice.id);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleDownloadPdf() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await getInvoiceDownloadUrl(invoice.id);
      window.open(res.url, '_blank', 'noopener');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setDownloadError('PDF is still generating by the background service. Please try again shortly.');
      } else {
        setDownloadError(apiErrorMessage(err) || 'Failed to open invoice PDF.');
      }
    } finally {
      setDownloading(false);
    }
  }

  function handleCopyNumber() {
    navigator.clipboard.writeText(invoice.invoiceNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const items = detail?.lineItems ?? [];
  const hasLineItems = items.length > 0;
  const isIgst = BigInt(invoice.igstPaise || '0') > 0n;

  return (
    <Dialog
      open
      onClose={onClose}
      closeOnOverlayClick
      className="max-w-2xl sm:max-w-3xl"
      title={
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base font-bold text-gray-900">
            {invoice.invoiceNumber}
          </span>
          <button
            type="button"
            onClick={handleCopyNumber}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            title="Copy invoice number"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>
          <span
            className={cn(
              'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase',
              TONE[invoice.status],
            )}
          >
            {invoice.status}
          </span>
          <span
            className={cn(
              'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium uppercase',
              MODE_TONE[invoice.paymentMode],
            )}
          >
            {invoice.paymentMode}
          </span>
        </div>
      }
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <Link
            to={`/transactions/${invoice.transactionId}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Go to Transaction ({invoice.transactionId.slice(0, 8)}…)
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="inline-flex items-center gap-1.5"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Opening PDF…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Download / View PDF
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-800">
        {downloadError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {downloadError}
          </div>
        ) : null}

        {/* Invoice Summary Grid */}
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50/70 p-4 sm:grid-cols-2">
          <div className="space-y-1.5 text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Supplier Details
            </div>
            <div className="font-semibold text-gray-900">{invoice.ecregiLegalName || 'ECREGI'}</div>
            <div className="text-gray-600">
              GSTIN: <span className="font-mono font-medium text-gray-900">{invoice.ecregiGstin || 'N/A'}</span>
            </div>
            <div className="text-gray-600">
              SAC Code: <span className="font-mono font-medium text-gray-900">{invoice.hsnSacCode || '998311'}</span>
            </div>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Client & Supply Details
            </div>
            <div className="text-gray-600">
              Place of Supply: <span className="font-semibold text-gray-900">{invoice.placeOfSupplyState || 'N/A'}</span>
            </div>
            <div className="text-gray-600">
              Client GSTIN: <span className="font-mono text-gray-900">{invoice.clientGstin || 'Unregistered (B2C)'}</span>
            </div>
            <div className="text-gray-600">
              Created: <span className="font-medium text-gray-900">{new Date(invoice.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              {invoice.paidAt ? (
                <span className="ml-2 font-medium text-emerald-700">
                  • Paid: {new Date(invoice.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">
            Line Items
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100 bg-gray-50/50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">#</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-center font-medium">Qty</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-gray-500">
                    <Loader2 className="mr-1 inline h-4 w-4 animate-spin text-gray-400" />
                    Loading line items…
                  </td>
                </tr>
              ) : hasLineItems ? (
                items.map((it, idx) => (
                  <tr key={it.id || idx}>
                    <td className="px-4 py-2.5 font-mono text-gray-400">{idx + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{it.description}</td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{it.quantity}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                      {formatPaise(it.amountPaise)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-2.5 font-mono text-gray-400">1</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">Professional Services</td>
                  <td className="px-4 py-2.5 text-center text-gray-600">1</td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                    {formatPaise(invoice.subtotalPaise)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Calculation / Total Breakdown */}
        <div className="flex justify-end">
          <div className="w-full sm:max-w-xs space-y-1.5 rounded-lg border border-gray-100 bg-gray-50 p-3.5 text-xs">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal:</span>
              <span className="font-medium text-gray-900">{formatPaise(invoice.subtotalPaise)}</span>
            </div>
            {isIgst ? (
              <div className="flex justify-between text-gray-600">
                <span>IGST ({invoice.gstPercentage}%):</span>
                <span className="font-medium text-gray-900">{formatPaise(invoice.igstPaise)}</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between text-gray-600">
                  <span>CGST:</span>
                  <span className="font-medium text-gray-900">{formatPaise(invoice.cgstPaise)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>SGST:</span>
                  <span className="font-medium text-gray-900">{formatPaise(invoice.sgstPaise)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-bold text-gray-900">
              <span>Total:</span>
              <span className="text-brand-700">{formatPaise(invoice.totalPaise)}</span>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
